import React, {
  createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState,
} from 'react';
import { BookLocation } from '../../dorian-shared/types/bookLocation/bookLocation';
import { useApiService } from '../ApiServiceContext/ApiServiceContext';

export type BookLocationsContextType = {
  bookLocations: BookLocation[] | null;
  fetchBookLocations: (bookId: number) => Promise<BookLocation[]>;
};

export const BookLocationsContext = createContext<BookLocationsContextType>({
  bookLocations: null,
  fetchBookLocations: async () => [],
});

export function BookLocationsContextProvider({ children }: { children: ReactNode }) {
  const [bookLocations, setBookLocations] = useState<BookLocation[] | null>(null);
  const locationsRef = useRef<BookLocation[] | null>(null);
  locationsRef.current = bookLocations;
  const inflightRef = useRef<Promise<BookLocation[]> | null>(null);
  const apiService = useApiService();

  const fetchBookLocations = useCallback(async (bookId: number) => {
    if (inflightRef.current) {
      return inflightRef.current;
    }
    const pending = (async () => {
      try {
        const locations = await apiService.fetchBookLocations(bookId);
        setBookLocations(locations);
        return locations;
      } finally {
        inflightRef.current = null;
      }
    })();
    inflightRef.current = pending;
    return pending;
  }, [apiService]);

  const value = useMemo(() => ({
    bookLocations,
    fetchBookLocations,
  }), [bookLocations, fetchBookLocations]);

  return (
    <BookLocationsContext.Provider value={value}>
      {children}
    </BookLocationsContext.Provider>
  );
}

export const useBookLocationsContext = () => useContext(BookLocationsContext);
